import { useState, useEffect } from "react";
import { Button, Tab, Tabs } from "@mui/material";
import { Box, Divider, InputLabel } from "@mui/material";

import useTabs from "~src/hooks/useTabs";
import { SearchBrandList } from "./SearchBrandList";
import { SearchProductList } from "./SearchProductList";
import { useInfiniteSearchProducts } from "./useInfiniteSearchProducts";
import { BrandAutocomplete } from "./BrandAutocomplete";
import { getBrandNames } from "~src/api/search";
import { useInfiniteTrendingAds } from "../ads/trending/useInfiniteTrendingAds";
import { TrendingAdsList } from "../ads/trending/TrendingAdsList";
import { PATH_ADS, PATH_BRANDS } from "~src/routes/paths";
import { useInfiniteBrandSearch } from "./useSearchBrands";

enum SearchTabs {
  Brand = "Brands",
  Products = "Products",
  FbAds = "FbAds",
}

export const SearchResultsList = ({ searchTerm }: { searchTerm: string }) => {
  const { currentTab, onChangeTab } = useTabs(SearchTabs.Brand);
  const [brandList, setBrandList] = useState<string[]>([]);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [optionsLoading, setOptionsLoading] = useState<boolean>(false);
  const productSearchContext = useInfiniteSearchProducts({
    selectedBrands,
    searchTerm,
    page: 1,
    pageSize: 20,
  });

  const trendingAdsContext = useInfiniteTrendingAds({
    page: 1,
    page_size: 30,
    search_text: searchTerm,
  });

  const brandSearchContext = useInfiniteBrandSearch({
    page: 1,
    page_size: 30,
    search_text: searchTerm,
  });

  const getSelectedBrands = (
    _e: React.ChangeEvent<unknown>,
    ...rest: any[]
  ) => {
    const brands = rest[0] as string[];
    if (brands.length > 0) {
      setSelectedBrands(brands);
    } else {
      setSelectedBrands([]);
    }
  };

  useEffect(() => {
    const loadBrandNames = async () => {
      setOptionsLoading(true);
      const response = await getBrandNames(searchTerm);
      setOptionsLoading(false);
      if (response) {
        setBrandList(response);
      }
    };

    loadBrandNames();
  }, [searchTerm]);

  return (
    <Box
      sx={{
        mx: 4,
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
        }}
      >
        <Tabs
          allowScrollButtonsMobile
          variant="scrollable"
          scrollButtons="auto"
          value={currentTab}
          onChange={onChangeTab}
          sx={{ mt: "auto" }}
        >
          <Tab
            disableRipple
            key={SearchTabs.Brand}
            label={
              brandSearchContext.total
                ? `Brands (${brandSearchContext.total.toLocaleString()})`
                : `Brands`
            }
            value={SearchTabs.Brand}
          />
          <Tab
            disableRipple
            key={SearchTabs.Products}
            label={
              productSearchContext.total
                ? `Products (${productSearchContext.total.toLocaleString()})`
                : `Products`
            }
            value={SearchTabs.Products}
          />
          <Tab
            disableRipple
            key={SearchTabs.FbAds}
            label={
              trendingAdsContext.total
                ? `Facebook Ads (${trendingAdsContext.total.toLocaleString()})`
                : `Facebook Ads`
            }
            value={SearchTabs.FbAds}
          />
        </Tabs>

        {currentTab === SearchTabs.Brand && (
          <Box
            sx={{
              ml: { xs: 0, md: "auto" },
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Button variant="contained" href={PATH_BRANDS.discover}>
              Discover Brands
            </Button>
          </Box>
        )}
        {currentTab === SearchTabs.Products && (
          <Box
            sx={{
              ml: { xs: 0, md: "auto" },
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <InputLabel sx={{ mr: { xs: 0, md: 2 }, mb: { xs: 1, md: 0 } }}>
              Show Products From{" "}
            </InputLabel>
            <BrandAutocomplete
              options={brandList}
              loading={optionsLoading}
              brandSelect={getSelectedBrands}
            />
          </Box>
        )}
        {currentTab === SearchTabs.FbAds && (
          <Box
            sx={{
              ml: { xs: 0, md: "auto" },
              display: "flex",
              alignItems: { xs: "flex-start", md: "center" },
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            <Button variant="contained" href={PATH_ADS.trending}>
              Ads Search
            </Button>
          </Box>
        )}
      </Box>

      <Divider
        sx={{
          borderColor: "grey.300",
          mb: 2,
        }}
      />
      <>
        {currentTab === SearchTabs.Brand && (
          <SearchBrandList brandSearchContext={brandSearchContext} />
        )}
        {currentTab === SearchTabs.Products && (
          <SearchProductList productSearchContext={productSearchContext} />
        )}
        {currentTab === SearchTabs.FbAds && (
          <TrendingAdsList trendingAdsContext={trendingAdsContext} />
        )}
      </>
    </Box>
  );
};
